// CAR-3634: single source of truth for user-facing error copy.
//
// Every error surfaced to a user should be routed through getFriendlyErrorMessage
// so we show consistent, friendly text with a safe fallback — and never leak raw
// text (stringified JSON, status enums like "FAILED", or JS errors such as
// "Cannot read properties of undefined (reading 'data')").

export const GENERIC_ERROR_FALLBACK = 'Something went wrong. Please try again.'

const NETWORK_ERROR = 'Network error. Check your internet connection and try again.'
const TIMEOUT_ERROR = 'The request timed out. Please try again.'

// CAR-3672: exact copy for the ADAS Intelligence scrub-job flow. The poll response
// (GET /v1/scrub-jobs/{id}) carries a stable `failureCode` on a failed job; map each
// classification to its approved message. Unknown/missing codes fall back to
// GENERAL_ERROR. See getScrubFailureMessage below.
export const SCRUB_FAILURE_MESSAGES = {
  TOO_LARGE: 'Unable to process — estimate exceeds the size limit. Please upload a smaller PDF.',
  IMAGE_UNREADABLE: 'Unable to read and process OCR. Please upload a text-based PDF estimate.',
  MAX_ATTEMPTS: 'Maximum processing attempts reached. Please upload a new estimate.',
  GENERAL_ERROR: 'Unable to process estimate. Please try again.'
}

// Shown when POST /v1/scrub-jobs/upload is rejected with HTTP 429 (backend caps
// uploads at 5 per minute per calibration center).
export const SCRUB_UPLOAD_RATE_LIMIT_MESSAGE = 'Too many uploads. Please wait a moment and try again.'

/** Map a scrub-job failureCode to its user-facing message, defaulting to the
 *  general-error copy for unknown or missing codes. */
export const getScrubFailureMessage = (failureCode) => SCRUB_FAILURE_MESSAGES[failureCode] || SCRUB_FAILURE_MESSAGES.GENERAL_ERROR

// Curated, system-voice copy per HTTP status. No "we", no "contact support".
const STATUS_MESSAGES = {
  400: "That request couldn't be processed. Please check your input and try again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  408: TIMEOUT_ERROR,
  409: 'That action conflicts with the current state. Please refresh and try again.',
  422: "Some of the information provided isn't valid. Please review and try again.",
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again.',
  502: 'Something went wrong on our end. Please try again.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: TIMEOUT_ERROR
}

// Decide whether a server-supplied string is safe to show verbatim: a short,
// human-readable sentence — not raw JSON, a status enum, or a JS error string.
const looksLikeHumanMessage = (value) => {
  if (typeof value !== 'string') return false
  const text = value.trim()
  if (text.length < 3 || text.length > 300) return false
  if (text.startsWith('{') || text.startsWith('[')) return false // raw JSON body
  if (/^[A-Z0-9_]+$/.test(text)) return false // status enum (FAILED, MAX_TOKENS, …)
  if (/cannot read propert|is not a function|undefined \(reading|null \(reading|\bTypeError\b|\bat\s.+:\d+/i.test(text)) {
    return false // JS error / stack trace leaking through
  }
  // Server-side exception dumps leaking through as a "4xx" message: an EXCEPTION:
  // prefix, an upstream connection/Feign failure, an embedded internal URL, or a
  // Java package/exception path (e.g. "java.net...", "com.caradas...Exception").
  if (
    /^exception[:\s]|connection refused|\bexecuting (?:GET|POST|PUT|PATCH|DELETE)\b|https?:\/\/(?:localhost|127\.0\.0\.1|[^\s/]*\.internal)|\b(?:java|org|com)\.[a-z0-9.]+(?:Exception|Error)\b/i.test(
      text
    )
  ) {
    return false
  }
  return true
}

const cleanServerMessage = (error) => {
  const data = error?.response?.data
  const candidate = looksLikeHumanMessage(data?.message) ? data.message : looksLikeHumanMessage(data) ? data : null
  if (!candidate) return null
  return candidate.trim().replace(/^Bad arguments sent:\s*/i, '')
}

/**
 * Map any error (axios error, JS error, or undefined) to friendly user-facing copy.
 * @param {*} error - the caught error
 * @param {string} [fallback] - context-specific fallback when nothing else matches
 * @returns {string} a safe, human-readable message — never raw error text
 */
export const getFriendlyErrorMessage = (error, fallback = GENERIC_ERROR_FALLBACK) => {
  // A caller that already resolved the exact copy (e.g. a mapped scrub failureCode
  // or a rate-limit case) attaches `friendlyMessage` to the thrown error; trust it
  // verbatim, still gated by looksLikeHumanMessage so nothing raw slips through.
  if (looksLikeHumanMessage(error?.friendlyMessage)) return error.friendlyMessage

  // No HTTP response → connectivity or timeout.
  if (error?.code === 'ERR_NETWORK' || (!error?.response && error?.request)) return NETWORK_ERROR
  if (error?.code === 'ECONNABORTED') return TIMEOUT_ERROR

  const status = error?.response?.status

  // Trust a clean server message only for client (4xx) errors — 5xx bodies may
  // leak internal/stack detail, so those always use curated copy.
  if (status >= 400 && status < 500) {
    const fromServer = cleanServerMessage(error)
    if (fromServer) return fromServer
  }

  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status]
  if (status >= 500) return STATUS_MESSAGES[500]

  return fallback
}
